import { Text } from "@react-three/drei";
import { createPortal, useThree } from "@react-three/fiber";
import { useEffect } from "react";
import { useRef } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FrontSide, Object3D, Quaternion, Vector3 } from "three";
import { degToRad } from "three/src/math/MathUtils";
import * as THREE from "three";
import { useSpring, animated } from "@react-spring/three";

const MeasurementLines = ({ node, radius = 0.00025, sidesLength = 0.005, fontSize = 0.0075, color = "#ffffff" }) => {
  const { scene } = useThree();

  const lineNodes = useMemo(() => {
    const ln: Object3D[] = [];
    node.traverse((c) => {
      if (c.name.startsWith("Measurement_")) {
        ln.push(c);
      }
    });
    return ln;
  }, []);

  return createPortal(
    <group>
      {lineNodes.map((ln, i) => (
        <MeasurementLine
          node={ln}
          key={`${node.name}_measurement_${i}`}
          radius={radius}
          sidesLength={sidesLength}
          fontSize={fontSize}
          color={color}
        />
      ))}
    </group>,
    scene,
  );
};

const MeasurementLine = ({ node, radius, sidesLength, fontSize, color }) => {
  const rootRef = useRef<THREE.Group>(null!);

  const { t } = useTranslation();

  const text = useMemo(() => {
    let text = "";

    if (node.userData.textId) {
      text = t(node.userData.textId);
    }

    return text;
  }, []);

  const length = useMemo(() => {
    return node.children[0].position.z * 2;
  }, []);

  useEffect(() => {
    const worldPos = new Vector3();
    const worldQuat = new Quaternion();
    node.getWorldPosition(worldPos);
    node.getWorldQuaternion(worldQuat);
    rootRef.current.position.copy(worldPos);
    rootRef.current.quaternion.copy(worldQuat);
  }, []);

  const { opacity } = useSpring({
    from: { opacity: 0 },
    to: [{ opacity: 1 }, { opacity: 1 }, { opacity: 1 }, { opacity: 1 }, { opacity: 0 }],
    config: { duration: 8000 },
    delay: 3000,
  });

  return (
    <group ref={rootRef}>
      <mesh rotation={[degToRad(90), 0, 0]}>
        <cylinderGeometry args={[radius, radius, length, 4, 1]} />
        <animated.meshStandardMaterial toneMapped={true} color={color} opacity={opacity} transparent={true} />
      </mesh>

      <mesh rotation={[degToRad(0), 0, 0]} position={[0, 0, -length / 2]}>
        <cylinderGeometry args={[radius, radius, sidesLength, 4, 1]} />
        <animated.meshStandardMaterial toneMapped={true} color={color} opacity={opacity} transparent={true} />
      </mesh>
      <mesh rotation={[degToRad(0), 0, 0]} position={[0, 0, length / 2]}>
        <cylinderGeometry args={[radius, radius, sidesLength, 4, 1]} />
        <animated.meshStandardMaterial toneMapped={true} color={color} opacity={opacity} transparent={true} />
      </mesh>

      <mesh rotation={[degToRad(0), 0, 0]} position={[0, -sidesLength / 2, 0]}>
        <cylinderGeometry args={[radius, radius, sidesLength, 4, 1]} />
        <animated.meshStandardMaterial toneMapped={true} color={color} opacity={opacity} transparent={true} />
      </mesh>

      <group position={[0, -sidesLength - fontSize, 0]}>
        <Text fontSize={fontSize} rotation={[0, degToRad(90), 0]}>
          {text}
          <animated.meshStandardMaterial toneMapped={true} color={color} opacity={opacity} transparent={true} side={FrontSide} />
        </Text>
        <Text fontSize={fontSize} rotation={[0, degToRad(-90), 0]}>
          {text}
          <animated.meshStandardMaterial toneMapped={true} color={color} opacity={opacity} transparent={true} side={FrontSide} />
        </Text>
      </group>
    </group>
  );
};

export default MeasurementLines;
